import React, { useState } from "react";
import { Map as MapIcon, Loader2, Check, AlertTriangle } from "lucide-react";
import { Tile, Badge } from "../Primitives";
import PhilippinesMap from "../PhilippinesMap";
import { statusOf } from "./shared";
import { setMapProvider, useMapConfig } from "../../lib/integrationsApi";
import { cn } from "../../lib/utils";

export default function MapProviderPanel({ services, active, canEdit, actor, onChanged }) {
  const cfg = useMapConfig();
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const maps = services.filter(s => s.category === "mapping");

  async function activate(id) {
    setBusy(id); setErr(null);
    try { await setMapProvider(id, actor); onChanged(); } catch (e) { setErr(e.message); } finally { setBusy(null); }
  }

  return (
    <Tile testid="map-provider-panel" title={<span className="text-[11px] font-semibold tracking-widest uppercase text-muted inline-flex items-center gap-2"><MapIcon size={12} className="text-primary" /> Branch Network basemap</span>}
          action={cfg && <Badge status={cfg.fallback_reason ? "warning" : "success"} testid="map-live-provider">serving: {cfg.label}</Badge>}>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
        <div className="xl:col-span-2 space-y-2">
          {maps.map((s, i) => {
            const st = statusOf(s);
            const isActive = s.service === active;
            const disabled = !canEdit || isActive || (!s.free && !s.configured) || !!busy;
            return (
              <button key={s.service} data-testid={`map-provider-${s.service}`} disabled={disabled}
                      onClick={() => activate(s.service)} style={{ animationDelay: `${i * 60}ms` }}
                      className={cn("w-full text-left px-3 py-2.5 border rounded-sm flex items-center gap-3 animate-fade-in transition-colors",
                        isActive ? "border-primary bg-primary/10" : "border-border hover:border-primary/60",
                        disabled && !isActive && "opacity-60 cursor-not-allowed")}>
                <span className={cn("w-4 h-4 rounded-full border flex items-center justify-center shrink-0",
                  isActive ? "border-primary bg-primary text-primary-fg" : "border-border-strong")}>
                  {busy === s.service ? <Loader2 size={9} className="animate-spin" /> : isActive && <Check size={9} />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-medium truncate">{s.label}</span>
                  <span className="block text-[10.5px] text-muted truncate">{s.free ? "No key · default fallback" : !s.configured ? "Add a key below to enable" : st.label}</span>
                </span>
                <Badge status={st.status}>{s.free ? "free" : s.configured ? "key saved" : "no key"}</Badge>
              </button>
            );
          })}
          {cfg?.fallback_reason && (
            <div data-testid="map-fallback-reason" className="text-[11px] text-warning bg-warning/5 border border-warning/30 rounded-sm p-2.5 flex gap-1.5 leading-snug">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              <span>Serving free tiles instead: {cfg.fallback_reason}</span>
            </div>
          )}
          {err && <div data-testid="map-provider-error" className="text-[11px] text-critical">{err}</div>}
          {!canEdit && <div className="text-[10.5px] text-subtle italic">Only the IT Head can switch providers.</div>}
        </div>
        <div className="xl:col-span-3 h-[300px] border border-border rounded-sm overflow-hidden" data-testid="map-preview">
          <PhilippinesMap onSelect={() => {}} />
        </div>
      </div>
    </Tile>
  );
}
